import swal from "sweetalert";
import axiosInstance from "../api";
import { setPromoPopups, setPromoPopupDetail } from "../../reusableSlice";

const POPUPS_URL = "admin/promo-popups/";

const showError = (message) =>
  swal({ text: message, icon: "error", button: "Close", timer: 1800 });

const showSuccess = (message) =>
  swal({ text: message, icon: "success", button: "Close", timer: 1500 });

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export const getPromoPopups = (params = {}, setLoading) => async (dispatch) => {
  setLoading?.(true);
  try {
    const response = await axiosInstance.get(POPUPS_URL, { params });
    if (response?.data?.status) {
      const raw = response.data.response;
      dispatch(
        setPromoPopups({
          data: Array.isArray(raw?.data) ? raw.data : [],
          pagination: raw?.pagination || null,
        }),
      );
    } else {
      dispatch(setPromoPopups({ data: [], pagination: null }));
    }
  } catch {
    dispatch(setPromoPopups({ data: [], pagination: null }));
  } finally {
    setLoading?.(false);
  }
};

export const getPromoPopupDetail = (id) => async (dispatch) => {
  try {
    const response = await axiosInstance.get(`${POPUPS_URL}${id}`);
    dispatch(setPromoPopupDetail(
      response?.data?.status ? response.data.response || null : null,
    ));
  } catch {
    dispatch(setPromoPopupDetail(null));
  }
};

export const createPromoPopup = (payload, setLoading, onSuccess) => async () => {
  setLoading?.(true);
  try {
    const response = await axiosInstance.post(POPUPS_URL, payload);
    if (response?.data?.status) {
      showSuccess(response.data.response?.message || "Popup created");
      onSuccess?.(response.data.response);
    } else {
      showError(response?.data?.message || "Failed to create popup");
    }
  } catch (err) {
    showError(errorMessage(err, "Failed to create popup"));
  } finally {
    setLoading?.(false);
  }
};

// Backend contract: partial update — `id` plus only the fields to change, in
// the body rather than the URL. Same shape as /admin/banners/ and
// /admin/sections/.
export const updatePromoPopup = (payload, setLoading, onSuccess) => async () => {
  setLoading?.(true);
  try {
    const response = await axiosInstance.put(POPUPS_URL, payload);
    if (response?.data?.status) {
      showSuccess(response.data.response?.message || "Popup updated");
      onSuccess?.(response.data.response);
    } else {
      showError(response?.data?.message || "Failed to update popup");
    }
  } catch (err) {
    showError(errorMessage(err, "Failed to update popup"));
  } finally {
    setLoading?.(false);
  }
};

export const deletePromoPopup = (id, setLoading, onSuccess) => async () => {
  setLoading?.(true);
  try {
    const response = await axiosInstance.delete(`${POPUPS_URL}${id}`);
    if (response?.data?.status) {
      showSuccess(response.data.response?.message || "Popup deleted");
      onSuccess?.();
    } else {
      showError(response?.data?.message || "Failed to delete popup");
    }
  } catch (err) {
    showError(errorMessage(err, "Failed to delete popup"));
  } finally {
    setLoading?.(false);
  }
};

/**
 * Coupons a popup is allowed to advertise.
 *
 * Read straight through rather than into redux: the offers list page owns
 * that slice, and filling it from a picker on a different screen would leave
 * the offers page showing a filtered subset the next time it opened.
 *
 * The filtering is client-side because /admin/offers has no offer_type
 * parameter and there are tens of offers, not thousands. The three conditions
 * are exactly what the backend's own save-time check enforces, so anything
 * this picker offers will be accepted:
 *   - offer_type 2, i.e. a coupon rather than an instant discount
 *   - an actual code to show
 *   - status 1, i.e. not deactivated (the list also returns status 2)
 *
 * An empty catalogue answers 422 "No offers found", which is a normal state
 * here and not worth a popup — the picker just reports that there are none.
 */
export const fetchCouponOptions = async () => {
  try {
    const response = await axiosInstance.get("admin/offers");
    if (!response?.data?.status) return [];
    const offers = response.data.response?.offers;
    if (!Array.isArray(offers)) return [];
    return offers.filter(
      (offer) =>
        Number(offer.offer_type) === 2 &&
        Number(offer.status) === 1 &&
        Boolean(offer.coupon_code),
    );
  } catch {
    return [];
  }
};
